import { Col, Dropdown, Input, Modal, Row, Table } from "antd";
import { debounce } from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as MoreActionsIcon } from "../../assets/more-actions.svg";
import CreateButton from "../../components/CreateButton";
import ErrorBanner from "../../components/ErrorBanner";
import { ScreenContext } from "../../contexts/ScreenContext";
import ProductController from "../../controllers/ProductController";
import AccountService from "../../services/AccountService";
import ImageService from "../../services/ImageService";
import ProductService from "../../services/ProductService";
import RoleCheckService from "../../services/RoleCheckService";
import styles from "./Products.module.css";
import ProductsCheckForUpdatesModal from "./ProductsCheckForUpdatesModal";
import ProductsModalForOrderGenerator from "./ProductsModalForOrderGenerator";
import "./ProductsModalForOrderGenerator.css";

const { Search } = Input;

const ProductsContext = createContext();

const ProductsProvider = ({ children }) => {
  const actualPageRef = useRef(1);
  return (
    <ProductsContext.Provider value={{ actualPageRef }}>
      {children}
    </ProductsContext.Provider>
  );
};

const Products = ({ user, alarm, ...rest }) => {
  const location = useLocation();
  const locationPathname = location.pathname;
  const navigate = useNavigate();

  const { t } = useTranslation("products");
  const productController = useRef(new ProductController(user));

  const actionListItems = useMemo(
    () => [
      { type: "enable", description: t("action0") },
      { type: "disable", description: t("action1") },
      { type: "edit", description: t("action2") },
      { type: "approve", description: t("action4") },
      { type: "reject", description: t("action3") },
      { type: "copy", description: t("action5") },
    ],
    [t],
  );

  const filterTextRef = useRef("");
  const [searchText, setSearchText] = useState("");
  const productsRef = useRef([]);

  const [packageIdLabel, setPackageIdLabel] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCheckForProductUpdate, setShowCheckForProductUpdate] =
    useState(false);
  const productApprovalRequired = user.account.productApprovalRequired;
  const [showConfirmRejectProduct, setShowConfirmRejectProduct] =
    useState(false);
  const [rejectedProduct, setRejectedProduct] = useState();

  const { handleShowSmScreenHeader, smallScreen } = useContext(ScreenContext);

  const { actualPageRef } = useContext(ProductsContext);

  const smallScreenQuery = useMediaQuery(smallScreen);
  const [modalInfoForOrderGenerator, setModalInfoForOrderGenerator] = useState({
    show: false,
    packageId: null,
    productPrimaryName: null,
  });

  let currentPage = actualPageRef.current;
  if (location?.state?.refresh === true) {
    currentPage = 1;
    actualPageRef.current = 1;
  }

  const tableParamsRef = useRef({
    pagination: {
      defaultCurrent: currentPage,
      current: currentPage,
      defaultPageSize: 10,
      pageSize: 10,
    },
  });

  const fetchAccount = async () => {
    try {
      const account = await AccountService.get(user);

      if (account?.fdbSettings?.showNDC) {
        setPackageIdLabel("NDC");
      } else if (account?.fdbSettings?.showDIN) {
        setPackageIdLabel("DIN");
      } else {
        setPackageIdLabel("");
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchAccount();
  }, []);

  useEffect(() => {
    smallScreenQuery && handleShowSmScreenHeader(true);
  }, [smallScreenQuery]);

  useEffect(() => {
    productController.current = new ProductController(user);
  }, [user]);

  useEffect(() => {
    async function actionFetchData() {
      if (alarm) {
        filterTextRef.current = alarm.callToActionRef;
        await handleSearch(productApprovalRequired);
      }
    }
    actionFetchData();
  }, [alarm?.callToActionRef]);

  async function fetchData() {
    await handleSearch(productApprovalRequired);
  }

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (location?.state?.refresh === true) {
      filterTextRef.current = "";
      setSearchText("");

      tableParamsRef.current.pagination.current = 1;
      actualPageRef.current = 1;

      fetchData();
    }
  }, [location?.state?.refresh]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (text) => {
        filterTextRef.current = text;
        tableParamsRef.current.pagination.current = 1;
        await handleSearch(productApprovalRequired);
      }, 500),
    [],
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  const handlePaginationChange = async (pagination, filters, sorter) => {
    actualPageRef.current = pagination.current;

    tableParamsRef.current = {
      pagination,
      filters,
      ...sorter,
    };

    if (pagination.pageSize !== tableParamsRef.current.pagination?.pageSize) {
      productsRef.current = [];
    }

    await handleSearch(productApprovalRequired);
  };

  const handleProductsResult = (error, data) => {
    if (error) {
      setError(error);
    } else {
      productsRef.current = data.products;
      tableParamsRef.current.pagination.total =
        data?.count ?? tableParamsRef.current.pagination.pageSize;
    }
    setLoading(false);
  };

  const handleSearch = async (isPendingFirst) => {
    const pagination = tableParamsRef.current.pagination;

    setLoading(true);
    filterTextRef.current = (filterTextRef.current ?? "").trim();
    try {
      const data = await productController.current.searchForProductsPaginated(
        filterTextRef.current,
        pagination.current,
        pagination.pageSize,
        isPendingFirst,
      );

      handleProductsResult(null, data);
    } catch (error) {
      handleProductsResult(error, null);
    }
  };

  const handleCreate = () => {
    const product = { isPending: productApprovalRequired, images: [] };
    navigate("create", {
      state: { product: product, isNew: true },
    });
  };

  const getActionMenu = (product) => {
    let listItems = [];

    if (!product.isEnabled) {
      //disabled
      if (RoleCheckService.canCreateProduct(user)) {
        listItems.push(actionListItems.find((o) => o.type === "enable"));
      }
    } else if (product.isPending) {
      //pending
      if (RoleCheckService.canCreateProduct(user)) {
        listItems.push(actionListItems.find((o) => o.type === "edit"));
      }

      if (RoleCheckService.deleteProducts(user)) {
        listItems.push(actionListItems.find((o) => o.type === "approve"));
      }

      if (RoleCheckService.deleteProducts(user)) {
        listItems.push(actionListItems.find((o) => o.type === "reject"));
      }

      listItems.push(actionListItems.find((o) => o.type === "copy"));
    } else {
      //normal
      if (RoleCheckService.canCreateProduct(user)) {
        listItems.push(actionListItems.find((o) => o.type === "edit"));
      }

      if (RoleCheckService.canCreateProduct(user)) {
        listItems.push(actionListItems.find((o) => o.type === "disable"));
      }

      listItems.push(actionListItems.find((o) => o.type === "copy"));
    }

    return listItems;
  };

  const handleAction = useCallback(
    (action, product) => {
      let newProduct = { ...product };
      switch (action.type) {
        case "enable":
          newProduct.isEnabled = true;
          handleEnable(newProduct);
          break;
        case "disable":
          newProduct.isEnabled = false;
          handleEnable(newProduct);
          break;
        case "edit": {
          const tempProduct = { ...product };
          if (productApprovalRequired) {
            tempProduct.isPending = true;
          }
          navigate("create", { state: { product: tempProduct } });
          break;
        }
        case "approve":
          newProduct.isPending = false;
          handleUpdate(newProduct);
          break;
        case "reject":
          setRejectedProduct(newProduct);
          setShowConfirmRejectProduct(true);
          break;
        case "copy":
          navigate("create", {
            state: { isCopy: true, isNew: true, product: product },
          });
          break;
        default:
          break;
      }
    },
    [navigate, productApprovalRequired],
  );

  const handlePullAllImagesFromFdb = () => {
    ImageService.pullAllFdbImages(user)
      .then(() => {})
      .catch((error) => {
        setError(error);
      });
  };

  const handleEnable = async (product) => {
    if (product.allowVBM) {
      product.updateVBM = true;
    }
    setLoading(true);
    ProductService.enable(user, product)
      .then((productDoc) => {
        let index = productsRef.current.findIndex(
          (o) => o._id === productDoc._id,
        );
        let list = [...productsRef.current];
        list[index] = productDoc;
        productsRef.current = list;
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleUpdate = (product) => {
    setLoading(true);
    ProductService.update(user, product)
      .then((productDoc) => {
        let index = productsRef.current.findIndex(
          (o) => o._id === productDoc._id,
        );
        let list = [...productsRef.current];
        list[index] = productDoc;
        productsRef.current = list;
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDelete = (product) => {
    setLoading(true);
    ProductService.delete(user, product)
      .then((productDoc) => {
        let list = [...productsRef.current];
        if (product.revision === 1) {
          let index = productsRef.current.findIndex(
            (o) => o._id === productDoc._id,
          );
          list.splice(index, 1);
        } else {
          let index = productsRef.current.findIndex(
            (o) => o._id === productDoc._id,
          );
          list[index] = productDoc;
        }

        productsRef.current = list;
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getRowClassName = (product) => {
    let className = ``;

    if (!product.isEnabled) {
      className += `${styles["Products__listitem--disabled"]}`;
    } else if (product.isPending) {
      className += `${styles["Products__listitem--pending"]}`;
    }

    return className;
  };

  const handleCloseModalForOrderGenerator = () => {
    setModalInfoForOrderGenerator((prevState) => {
      return {
        ...prevState,
        show: false,
      };
    });
  };

  return (
    <>
      {
        // locationPathname === "/dispensing-orders/order-generation" &&
        showConfirmRejectProduct && (
          <Modal
            title={t("rejectTitle")}
            open={showConfirmRejectProduct}
            onCancel={() => {
              setRejectedProduct(null);
              setShowConfirmRejectProduct(false);
            }}
            onOk={() => {
              handleDelete(rejectedProduct);
              setRejectedProduct(null);
              setShowConfirmRejectProduct(false);
            }}
          >
            {t("rejectConfirm")}
          </Modal>
        )
      }

      <ProductsCheckForUpdatesModal
        user={user}
        open={showCheckForProductUpdate}
        onCancel={() => {
          setShowCheckForProductUpdate(false);
        }}
        onOk={() => {
          setShowCheckForProductUpdate(false);
        }}
        onError={(error) => {
          setError(error);
        }}
      />

      <Outlet />
      {error && (
        <div className={styles.Products__errorBannerContainer}>
          <ErrorBanner message={error} onClose={() => setError(null)} />
        </div>
      )}

      {(locationPathname === "/settings/products" ||
        locationPathname === "/inventory/medications" ||
        locationPathname === "/dispensing-orders/order-generation") && (
        <Row gutter={5} align={"middle"}>
          {!smallScreenQuery && (
            <Col span={24} style={{ height: "70px" }}></Col>
          )}
          <Col span={smallScreenQuery ? 24 : 12}>
            <Search
              placeholder={
                smallScreenQuery
                  ? t("searchPlaceholderMobile")
                  : t("searchPlaceholder")
              }
              value={searchText}
              allowClear
              onChange={(e) => {
                const v = e.target.value;
                setSearchText(v);
                debouncedSearch(v);
              }}
            />
          </Col>

          <Col span={smallScreenQuery ? 24 : 12}>
            <Row justify={"end"}>
              {/* <Col span={smallScreenQuery ? 24 : undefined}>
                {RoleCheckService.isSuperAdmin(user) &&
                  locationPathname !==
                    "/dispensing-orders/order-generation" && (
                    <CreateButton
                      onClick={async () => {
                        setShowCheckForProductUpdate(true);
                      }}
                      isEnabled={RoleCheckService.canUserCheckForUpdates(user)}
                      labelName={"Check for updates"}
                    />
                  )}
              </Col> */}

              <Col span={smallScreenQuery ? 24 : undefined}>
                {RoleCheckService.isSuperAdmin(user) &&
                  locationPathname !==
                    "/dispensing-orders/order-generation" && (
                    <CreateButton
                      onClick={handlePullAllImagesFromFdb}
                      isEnabled={true}
                      labelName={t("pullImagesLabel")}
                    />
                  )}
              </Col>

              <Col span={smallScreenQuery ? 24 : undefined}>
                {RoleCheckService.canCreateProduct(user) &&
                  locationPathname !==
                    "/dispensing-orders/order-generation" && (
                    <CreateButton
                      onClick={handleCreate}
                      isEnabled={true}
                      labelName={t("createButtonLabel")}
                    />
                  )}
              </Col>
            </Row>
          </Col>

          <Col span={24}>
            {modalInfoForOrderGenerator.show && (
              <div className="ProductsModalForOrderGenerator__container">
                <ProductsModalForOrderGenerator
                  onCloseModalForOrderGenerator={
                    handleCloseModalForOrderGenerator
                  }
                  onPackageId={rest.onPackageId}
                  packageId={modalInfoForOrderGenerator.packageId}
                  productPrimaryName={
                    modalInfoForOrderGenerator.productPrimaryName
                  }
                  onHideProductsTable={rest.onHideProductsTable}
                />
              </div>
            )}
            <Table
              onRow={
                rest?.onPackageId && rest?.onHideProductsTable
                  ? (record, rowIndex) => {
                      return {
                        onClick: () => {
                          setModalInfoForOrderGenerator((prevState) => {
                            return {
                              packageId: record?.packageId,
                              productPrimaryName: record?.primaryName,
                              show: true,
                            };
                          });
                        },
                      };
                    }
                  : null
              }
              loading={loading}
              columns={[
                {
                  title: packageIdLabel ? packageIdLabel : t("col0"), //Id
                  render: (p) => p.packageId,
                  width: 130,
                  ellipsis: true,
                },
                {
                  title: t("col1"), //Description
                  render: (p) =>
                    `${productController?.current?.getDefaultName(p) ?? ""}`,
                  ellipsis: true,
                },
                {
                  title: t("col6"), //Form
                  render: (p) =>
                    `${p.dosageForm?.type ?? p.dosageForm?.description ?? ""}`,
                  ellipsis: true,
                  responsive: ["xxl", "xl"],
                },
                {
                  title: t("col3"), //Manufacturer
                  render: (p) =>
                    `${p.manufacturer?.fullName || p.manufacturer?.abbrName}`,
                  ellipsis: true,
                },
                {
                  title: t("col5"), //Label ID
                  render: (p) => `${p.humanReadableId}`,
                  ellipsis: true,
                  responsive: ["xxl", "xl"],
                  width: 130,
                },
                {
                  title: t("col4"), //Actions
                  width: 100,
                  align: "center",
                  render: (product) =>
                    RoleCheckService.canCreateProduct(user) ? (
                      <Dropdown
                        trigger={["click"]}
                        menu={{
                          items: getActionMenu(product).map((item) => ({
                            key: item.action,
                            label: (
                              <div onClick={() => handleAction(item, product)}>
                                {item.description}
                              </div>
                            ),
                          })),
                        }}
                      >
                        <div style={{ cursor: "pointer", margin: "4px" }}>
                          <MoreActionsIcon />
                        </div>
                      </Dropdown>
                    ) : null,
                },
              ]}
              rowKey={(record) => record._id}
              dataSource={productsRef.current}
              pagination={tableParamsRef.current.pagination}
              onChange={handlePaginationChange}
              scroll={
                !productsRef.current || productsRef.current.length === 0
                  ? undefined
                  : { y: window.innerHeight - 320 }
              }
              rowClassName={getRowClassName}
            />
          </Col>
        </Row>
      )}
    </>
  );
};

const ProductsWithContext = (props) => (
  <ProductsProvider>
    <Products {...props} />
  </ProductsProvider>
);

export default ProductsWithContext;
